import { DevicePlayerInfoProps } from "@/app/activity/detail/devicePlayerInfo/type"

import "./devicePlayerInfo.scss"
import BasicButton from "@/components/button/BasicButton"
import Chip from "@/components/chip/Chip"
import { useEffect, useMemo, useState } from "react"
import Checkbox from "@/components/form/checkbox/Checkbox"
import classNames from "classnames"
import { useClickDataMatchToPlayerAction } from "@/app/activity/detail/devicePlayerInfo/hooks"
import { createActivityCommonStore, createSessionStore } from "@/app/activity/store"
import { useSearchParams } from "@/hooks/useSearchParams"

export default function DevicePlayerInfo({
  activityData,
  className,
  isCropping = false,
  teamDeviceList,
  playerGroupName,
  ...props
}: DevicePlayerInfoProps) {
  const [isChanged, setIsChanged] = useState<boolean>(false)
  const isMatched = activityData.matchedToActivity

  const deviceSerial = useMemo(() => {
    const { deviceModel, deviceNumber, deviceVersion } = activityData

    return `${deviceModel}-${deviceVersion}-${deviceNumber.toString().padStart(5, "0")}`
  }, [activityData])

  const teamPlayer = useMemo(() => {
    if (!activityData.idfPlayer) return null

    return teamDeviceList?.find((device) => device.idfPlayer === activityData.idfPlayer)
  }, [teamDeviceList, activityData.idfPlayer])

  const player = useMemo(() => {
    if (activityData.idfPlayer && teamPlayer) {
      return {
        backNumber: teamPlayer.uniformNumber || teamPlayer.numberUniform || teamPlayer.backNumber,
        nickname: teamPlayer.nickname || teamPlayer.nickName,
      }
    }

    return {
      backNumber: activityData.devicePlayer.backNumber,
      nickname: activityData.devicePlayer.nickname || activityData.devicePlayer.nickName,
    }
  }, [activityData.idfPlayer, activityData.devicePlayer, teamPlayer])

  const params = useSearchParams()

  const { setIdfDataForMatch, playerRemoveDialogProps } = createActivityCommonStore((state) => ({
    setIdfDataForMatch: state.setIdfDataForMatch,
    playerRemoveDialogProps: state.playerRemoveDialogProps,
  }))

  const { selectedSession } = createSessionStore((state) => ({
    selectedSession: state.session,
  }))

  useClickDataMatchToPlayerAction(
    activityData.idfData,
    activityData,
    isMatched,
    isChanged,
    Number(params.get("activity")),
  )
  const dataTypes = useMemo(() => {
    const textByType = [
      { text: "GNSS", value: "existGps", color: "" },
      { text: "IMU", value: "existImu", color: "" },
      { text: "HR", value: "existHr", color: "" },
    ]

    return textByType.map((type) => ({
      ...type,
      color: activityData[type.value as "existGps" | "existImu" | "existHr"]
        ? "primary--text"
        : "grey--text",
    }))
  }, [activityData])

  useEffect(() => {
    if (playerRemoveDialogProps === undefined) {
      setIsChanged(false)
    }
  }, [playerRemoveDialogProps])

  return (
    <div className={classNames("DevicePlayerInfoCont", className)} {...props}>
      <Checkbox
        checked={isMatched}
        disabled={isCropping || !!selectedSession || (!isMatched && !activityData.idfPlayer)}
        onClick={() => {
          if (!isMatched && !activityData.idfPlayer) {
            return
          }

          setIsChanged(true)
        }}
      />
      <div className="player-info">
        <div className="player-name-section">
          <p className="player-name">
            {player?.backNumber} {player?.nickname}
          </p>
        </div>
        <div className="device-info">
          <BasicButton
            type="ghost"
            size="sm"
            onClick={() => setIdfDataForMatch(activityData)}
            disabled={isCropping || !!selectedSession}>
            <Chip label={deviceSerial} type="serial" disabled={isCropping || !!selectedSession} />
          </BasicButton>
          {dataTypes && (
            <p className="data-types">
              {dataTypes.map((type, index) => (
                <span key={index} className={type.color}>
                  {type.text}
                </span>
              ))}
            </p>
          )}
        </div>
      </div>
    </div>
  )
}
